import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, X, Pencil, Check, ChevronLeft, ChevronRight, Camera, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { fileUrl, uploadFileChunked } from "@/lib/uploads";
import { compressImage } from "@/lib/imageCompress";
import { bgByKey } from "@/lib/storyBg";
import HighlightEditor from "@/components/social/HighlightEditor";
import StoryViewer from "@/components/social/StoryViewer";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const GOLD_RING = "linear-gradient(135deg,#F3D56D,#D4AF37,#8a6d1f)";

function Cover({ h }) {
  if (h.cover) return <img src={fileUrl(h.cover)} alt="" className="h-full w-full object-cover" />;
  const first = (h.items || [])[0];
  if (first?.type === "text") { const bg = bgByKey(first.bg); return <div className="h-full w-full" style={{ background: bg.css }} />; }
  if (first?.media) return <img src={fileUrl(first.poster || first.media)} alt="" className="h-full w-full object-cover" />;
  return <div className="h-full w-full bg-[var(--rc-bg)]" />;
}

export default function HighlightsRow({ userId, isMe, authorName, authorAvatar }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [manage, setManage] = useState(false);
  const [coverBusy, setCoverBusy] = useState(null);
  const coverRef = useRef(null);
  const coverTarget = useRef(null);

  const load = useCallback(() => { api.get(`/social/highlights/${userId}`).then(({ data }) => setItems(data)).catch(() => {}); }, [userId]);
  useEffect(() => { load(); }, [load]);

  const open = (h) => {
    if (manage) return;
    if (!h.items?.length) { toast.message(t("st_empty_highlight")); return; }
    const group = {
      author_id: userId, author_name: authorName, author_avatar: authorAvatar, is_me: isMe,
      stories: h.items.map((it) => ({ id: it.item_id, type: it.type, media: it.media, poster: it.poster, blur_hash: it.blur_hash, text: it.text, bg: it.bg, text_style: it.text_style, overlays: it.overlays, drawing: it.drawing, music: it.music, music_title: it.music_title, duration: it.duration, created_at: it.saved_at, view_count: 0 })),
    };
    setViewer({ groups: [group], startGroup: 0 });
  };

  const del = async (e, h) => {
    e.stopPropagation();
    if (!window.confirm(t("st_delete_hl_q"))) return;
    try { await api.delete(`/social/highlights/${h.id}`); setItems((arr) => arr.filter((x) => x.id !== h.id)); toast.success(t("st_hl_deleted")); }
    catch { toast.error(t("st_failed")); }
  };

  const move = (idx, dir) => {
    setItems((arr) => {
      const next = [...arr];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return arr;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const openCoverPicker = (e, h) => { e.stopPropagation(); coverTarget.current = h.id; coverRef.current?.click(); };
  const pickCover = async (e) => {
    let f = e.target.files?.[0]; const hid = coverTarget.current;
    if (!f || !hid) return;
    setCoverBusy(hid);
    try {
      f = await compressImage(f);
      const { url } = await uploadFileChunked(f, null, "social");
      const { data } = await api.put(`/social/highlights/${hid}`, { cover: url });
      setItems((arr) => arr.map((x) => x.id === hid ? { ...x, cover: data.cover } : x));
      toast.success(t("st_cover_updated"));
    } catch { toast.error(t("st_cover_failed")); }
    finally { setCoverBusy(null); coverTarget.current = null; e.target.value = ""; }
  };

  const saveOrder = async () => {
    setManage(false);
    try { await api.put("/social/highlights-reorder", { order: items.map((x) => x.id) }); toast.success(t("st_order_saved")); }
    catch { toast.error(t("st_failed")); load(); }
  };

  if (!isMe && items.length === 0) return null;

  return (
    <div className="mt-4" data-testid="highlights-row">
      <input ref={coverRef} type="file" accept="image/*" hidden onChange={pickCover} data-testid="highlight-cover-file" />
      {isMe && items.length > 0 && (
        <div className="flex justify-end px-1 mb-1">
          {manage
            ? <button onClick={saveOrder} data-testid="highlights-done-btn" className="flex items-center gap-1 text-xs text-[var(--rc-gold)] font-semibold"><Check size={14} /> {t("st_done")}</button>
            : <button onClick={() => setManage(true)} data-testid="highlights-manage-btn" className="flex items-center gap-1 text-xs text-[var(--rc-muted)]"><Pencil size={13} /> {t("st_edit")}</button>}
        </div>
      )}
      <div className="flex gap-4 overflow-x-auto no-scrollbar px-1">
        {isMe && !manage && (
          <button onClick={() => setEditor(true)} data-testid="highlight-add-btn" className="shrink-0 flex flex-col items-center gap-1.5 w-[70px]">
            <div className="h-[62px] w-[62px] rounded-full grid place-items-center border-2 border-dashed border-[var(--rc-gold)]/50 text-[var(--rc-gold)]"><Plus size={22} /></div>
            <span className="text-[11px] text-[var(--rc-silver)]">{t("st_new")}</span>
          </button>
        )}
        {items.map((h, idx) => (
          <div key={h.id} onClick={() => open(h)} data-testid={`highlight-${h.id}`} role="button" className="shrink-0 flex flex-col items-center gap-1.5 w-[70px] cursor-pointer">
            <div className="relative rounded-full p-[2.5px]" style={{ background: GOLD_RING }}>
              <div className="rounded-full p-[2px] bg-[var(--rc-bg)]">
                <div className="h-[56px] w-[56px] rounded-full overflow-hidden relative">
                  <Cover h={h} />
                  {coverBusy === h.id && <div className="absolute inset-0 grid place-items-center bg-black/50"><Loader2 size={16} className="animate-spin text-[var(--rc-gold)]" /></div>}
                  {manage && coverBusy !== h.id && (
                    <button onClick={(e) => openCoverPicker(e, h)} data-testid={`highlight-cover-${h.id}`} className="absolute inset-0 grid place-items-center bg-black/45 text-white"><Camera size={18} /></button>
                  )}
                </div>
              </div>
              {isMe && !manage && <button onClick={(e) => del(e, h)} data-testid={`highlight-del-${h.id}`} className="absolute -top-1 -right-1 h-[20px] w-[20px] grid place-items-center rounded-full bg-black/70 text-white border border-white/20"><X size={12} /></button>}
            </div>
            {manage ? (
              <div className="flex items-center gap-1">
                <button onClick={(e) => { e.stopPropagation(); move(idx, -1); }} disabled={idx === 0} data-testid={`highlight-left-${h.id}`} className="h-6 w-6 grid place-items-center rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] disabled:opacity-30"><ChevronLeft size={13} /></button>
                <button onClick={(e) => { e.stopPropagation(); move(idx, 1); }} disabled={idx === items.length - 1} data-testid={`highlight-right-${h.id}`} className="h-6 w-6 grid place-items-center rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] disabled:opacity-30"><ChevronRight size={13} /></button>
              </div>
            ) : (
              <span className="text-[11px] text-[var(--rc-text)] truncate max-w-[66px]">{h.title}</span>
            )}
          </div>
        ))}
      </div>
      {editor && <HighlightEditor onClose={() => setEditor(false)} onCreated={() => load()} />}
      {viewer && <StoryViewer groups={viewer.groups} startGroup={0} onClose={() => setViewer(null)} readOnly />}
    </div>
  );
}
